// How sensitive is a capability? One ladder, not two overlapping vocabularies.
//
// An earlier version mixed *type* of access ("Configuration change") with
// *kind* of authority ("Workflow authority") in the same badge row, which
// asked the reader to hold two classification schemes at once. This is a
// single ladder, least to most consequential:
//
//   standard        read a screen, add a note — the overwhelming majority
//   configuration   changes how cost is measured, allocated or reported
//   approval        acts inside an approval workflow
//   period          closes or reopens a period, freezing everyone's numbers
//   sensitive       reads or exports figures not everyone may see
//   administrative  grants access to others, or bypasses the controls
//
// Keyed by capability code (the backend's authz.Registry codes) rather than
// derived from the catalog's `risk` tier: risk="elevated" covers "Prepare
// budget changes" and "Configure dimensions" alike, and what an administrator
// needs before handing a role out is *which kind* of elevation it is.
export const ACCESS_CONFIG = "configuration";
export const ACCESS_APPROVAL = "approval";
export const ACCESS_PERIOD = "period";
export const ACCESS_SENSITIVE = "sensitive";
export const ACCESS_ADMIN = "administrative";

const ACCESS_BY_CODE = {
  "finance.view_sensitive": ACCESS_SENSITIVE,
  "audit.export": ACCESS_SENSITIVE,
  "secrets.manage": ACCESS_SENSITIVE,

  "budget.submit": ACCESS_APPROVAL,
  "config.budget_policies": ACCESS_APPROVAL,

  "period.lock": ACCESS_PERIOD,

  "config.dimensions": ACCESS_CONFIG,
  "config.allocation_models": ACCESS_CONFIG,
  "config.allocation_propose": ACCESS_CONFIG,
  "config.reporting": ACCESS_CONFIG,
  "integrations.manage": ACCESS_CONFIG,
  "sso.configure": ACCESS_CONFIG,
  "tenant.configure": ACCESS_CONFIG,

  "users.manage": ACCESS_ADMIN,
  "roles.assign": ACCESS_ADMIN,
  "service_accounts.manage": ACCESS_ADMIN,
  "tenant.emergency_access": ACCESS_ADMIN,
};

// Row badges — the top four levels only.
//
// `configuration` deliberately has no badge. On a configuration-heavy
// template it produced six identical grey pills down the Configuration
// section, competing with the capability names for attention while telling
// the reader nothing the section heading hadn't already said. It is stated
// once per section instead (see accessSummary), which leaves a row badge
// meaning "this one is the exception" again.
//
// Colour escalates and is spent sparingly: muted blue for authority over a
// workflow, amber for data not everyone may see, red only for access that
// hands out access.
const BADGE = {
  [ACCESS_APPROVAL]: { label: "Approval authority", tone: "info" },
  [ACCESS_PERIOD]: { label: "Period control", tone: "info" },
  [ACCESS_SENSITIVE]: { label: "Sensitive financial data", tone: "warning" },
  [ACCESS_ADMIN]: { label: "Administrative access", tone: "danger" },
};

// Sentences are coarser than badges on purpose. A row says exactly what it is
// ("Period control"); a summary sentence listing four near-synonyms reads as
// legalese, so period control rolls up into approval authority there. The
// precision stays where it is actionable — on the row.
const SUMMARY_ORDER = [ACCESS_ADMIN, ACCESS_SENSITIVE, ACCESS_APPROVAL, ACCESS_PERIOD, ACCESS_CONFIG];
const SUMMARY_PHRASE = {
  [ACCESS_ADMIN]: "administrative access",
  [ACCESS_SENSITIVE]: "sensitive financial data",
  [ACCESS_APPROVAL]: "approval authority",
  [ACCESS_PERIOD]: "approval authority",
  [ACCESS_CONFIG]: "configuration access",
};

// The access level for one capability code, or undefined for the majority
// that sit at `standard`.
export function accessLevel(code) {
  return ACCESS_BY_CODE[code];
}

// Badge props ({label, tone}) for a capability code, or null when the
// capability carries no badge — which is both `standard` and `configuration`.
export function accessBadge(code) {
  return BADGE[accessLevel(code)] || null;
}

// One sentence naming what a set of capabilities adds up to, used both for the
// drawer's callout and for a single section's descriptor. Returns null when
// everything in the set is standard, so the caller renders nothing at all
// rather than "Includes nothing unusual" — a reassurance on the quiet
// templates only trains the eye to skip the sentence on the loud ones.
export function accessSummary(capabilities) {
  const levels = new Set((capabilities || []).map(accessLevel));
  const phrases = [];
  for (const level of SUMMARY_ORDER) {
    const phrase = SUMMARY_PHRASE[level];
    if (levels.has(level) && !phrases.includes(phrase)) phrases.push(phrase);
  }

  if (phrases.length === 0) return null;
  const list =
    phrases.length === 1
      ? phrases[0]
      : `${phrases.slice(0, -1).join(", ")} and ${phrases[phrases.length - 1]}`;
  return `Includes ${list}.`;
}

// Whether a set of capabilities reaches the amber tier. The drawer's callout
// uses it to title itself, naming financial data only when financial data is
// actually in play.
export function includesSensitiveData(capabilities) {
  return (capabilities || []).some((code) => accessLevel(code) === ACCESS_SENSITIVE);
}
